import { useEffect, useState } from "react";
import Lenis from "lenis";
import "lenis/dist/lenis.css";
import "@/App.css";
import Nav from "@/components/Nav";
import Marquee from "@/components/Marquee";
import LiveDemo from "@/components/LiveDemo";
import AuthModal from "@/components/AuthModal";
import ProfileModal from "@/components/ProfileModal";
import AdminModal from "@/components/AdminModal";
import EmberCanvas from "@/components/EmberCanvas";
import WarningBanner from "@/components/WarningBanner";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { api } from "@/lib/api";

function App() {
  const [user, setUser] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [emCount, setEmCount] = useState(0);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
    toast.success("Signed out");
  };

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  if (user === null) {
    return (
      <div className="grid min-h-screen place-items-center bg-[#0A0A0A]">
        <p className="font-jb text-[11px] tracking-[0.35em] text-zinc-500">
          <span className="signal-dot mr-2 inline-block h-1.5 w-1.5 rounded-full bg-[#FF3B30]" />
          ACQUIRING SIGNAL…
        </p>
      </div>
    );
  }

  if (user === false) {
    return (
      <div
        data-testid="auth-gate"
        className="relative grid min-h-screen place-items-center overflow-hidden bg-[#0A0A0A]"
      >
        <div className="noise-overlay" aria-hidden="true" />
        <EmberCanvas />
        <div className="relative z-10 px-5 text-center">
          <p className="font-chivo text-4xl font-black tracking-tighter text-white sm:text-5xl">
            PRIVATE<span className="text-[#FF3B30]"> FIRE ALERTS</span>
          </p>
          <p className="mt-4 font-jb text-[10px] tracking-[0.35em] text-zinc-500">
            MEMBERS ONLY — LOG IN OR REQUEST ACCESS
          </p>
        </div>
        <AuthModal open locked onClose={() => {}} onAuth={setUser} />
        <Toaster theme="dark" position="bottom-right" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] font-plex text-zinc-100">
      <div className="noise-overlay" aria-hidden="true" />
      <WarningBanner onChange={setEmCount} />
      <Nav
        user={user}
        onLogin={() => setAuthOpen(true)}
        onLogout={logout}
        onProfile={() => setProfileOpen(true)}
        onAdmin={() => setAdminOpen(true)}
        bannerOffset={emCount > 0}
      />
      <main>
        <Marquee />
        <LiveDemo user={user} />
      </main>
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} onAuth={setUser} />
      <ProfileModal
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        user={user}
        onAuth={setUser}
      />
      <AdminModal open={adminOpen} onClose={() => setAdminOpen(false)} />
      <Toaster theme="dark" position="bottom-right" />
    </div>
  );
}

export default App;
